import React, { useCallback, useEffect, useRef, MouseEvent, useMemo } from 'react';
import classNames from 'classnames';
import './DesignerActions.less';
import { DesignerAction, IDesignerAction } from './DesignerAction';
import { GetPriorityCallback, usePriorityResize } from 'hooks/usePriorityResize';
import { IDesignerSubAction } from './DesignerSubActions';
import { debounce } from 'debounce';
import { useDisableAction } from 'hooks/useDisableAction';

export interface IDesignerActionsProps extends Omit<React.ComponentProps<'ul'>, 'children'> {
  actions: IDesignerAction[];
  /**
   * "white" theme is used for flyout menu.
   * "orange" theme is used for header actions.
   */
  theme: 'white' | 'orange';
  /**
   * get all free space in line
   */
  isGrowing?: boolean;
  /**
   * By default if there is no enough space "More" button will be added which will contain actions which cannot be displayed.
   * This property can be used to turn off this behavior.
   *
   * @default true
   */
  autoCollapse?: boolean;
}

function getActionsClassName(theme: string, isGrowing?: boolean) {
  return classNames(
    'dpc-designer-actions',
    { 'dpc-designer-actions--grow': isGrowing },
    { 'dpc-designer-actions--white': theme === 'white' },
    { 'dpc-designer-actions--orange': theme === 'orange' }
  );
}

// reserved space for MORE button
const moreButtonWidth = 115;
const noActions: IDesignerAction[] = [];

/**
 * Displays actions in designer header
 */
export function DesignerActions({
  theme,
  actions,
  isGrowing,
  autoCollapse = true,
  ...otherProps
}: IDesignerActionsProps) {
  const rootElement = useRef<HTMLUListElement | null>(null);

  const getChildren = useCallback(() => {
    if (rootElement.current != null) {
      return [...Array.from(rootElement.current.children)] as HTMLElement[];
    }
    return [];
  }, []);

  const getPriority: GetPriorityCallback = useCallback(
    ({ index }) => {
      if (actions?.[index]) {
        // do not hide actions with sub actions, to do not implement sub actions for sub actions
        return actions?.[index].subActions ? 1 : 0;
      }
      return 0;
    },
    [actions]
  );

  const getAvailableSpace = useCallback(
    (isResized: boolean) => {
      if (!autoCollapse) {
        return Number.POSITIVE_INFINITY;
      }

      let result = rootElement.current ? rootElement.current.getBoundingClientRect().width : 0;

      if (isResized) {
        result -= moreButtonWidth;
      }

      return result;
    },
    [autoCollapse]
  );

  const [visibleItems, updateVisibleItems] = usePriorityResize(
    actions ?? noActions,
    getAvailableSpace,
    getChildren,
    getPriority
  );

  const resize = useMemo(() => debounce(updateVisibleItems, 200), [updateVisibleItems]);

  useEffect(() => {
    if (autoCollapse) {
      resize();
      window.addEventListener('resize', resize);
      return () => {
        window.removeEventListener('resize', resize);
      };
    }
  }, [resize, updateVisibleItems, actions, autoCollapse]);

  //TODO alex: move moreItems to usePriorityResize
  const moreItems = useMemo(() => {
    return (actions?.filter(link => visibleItems.indexOf(link) === -1) as IDesignerSubAction[]) ?? [];
  }, [actions, visibleItems]);

  const { disabledIds, handleClick } = useDisableAction();

  return (
    <ul ref={rootElement} className={getActionsClassName(theme, isGrowing)} {...otherProps}>
      {visibleItems
        ?.filter(item => item)
        .map(({ action, disabled, ...rest }) => (
          <DesignerAction
            key={rest.id?.length ? rest.id : rest.name}
            disabled={disabledIds.includes(rest.name) || disabled}
            action={
              typeof action === 'function'
                ? (e?: MouseEvent<HTMLButtonElement>) => handleClick(e, action, rest.name)
                : undefined
            }
            {...rest}
          />
        ))}
      {actions.length !== visibleItems.length && <DesignerAction name="More" subActions={moreItems} />}
    </ul>
  );
}
